import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CurrencyDisplay } from '@/components/ui/stat-card';
import { FileText, Car, User, Phone, Settings, AlertTriangle, AlertOctagon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import ContractInsightOverview from '@/components/contracts/insights/ContractInsightOverview';
import ContractInsightPayments from '@/components/contracts/insights/ContractInsightPayments';
import ContractInsightVehicle from '@/components/contracts/insights/ContractInsightVehicle';
import ContractInsightInterest from '@/components/contracts/insights/ContractInsightInterest';

import ContractInsightLoans from '@/components/contracts/insights/ContractInsightLoans';
import ContractInsightInsurance from '@/components/contracts/insights/ContractInsightInsurance';
import ContractInsightDocuments from '@/components/contracts/insights/ContractInsightDocuments';
import ContractInsightMaintenance from '@/components/contracts/insights/ContractInsightMaintenance';
import ContractInsightInspections from '@/components/contracts/insights/ContractInsightInspections';
import ContractInsightRoadworthiness from '@/components/contracts/insights/ContractInsightRoadworthiness';
import ContractInsightTrackers from '@/components/contracts/insights/ContractInsightTrackers';
import ManageContractSheet from '@/components/contracts/insights/ManageContractSheet';
import TerminateContractDialog from '@/components/contracts/insights/TerminateContractDialog';
import CompleteContractDialog from '@/components/contracts/insights/CompleteContractDialog';
import AddMaintenanceSheet from '@/components/contracts/AddMaintenanceSheet';
import AddRoadworthinessWizard from '@/components/roadworthiness/wizard/AddRoadworthinessWizard';
import TrackerForm from '@/components/contracts/preparation/TrackerForm';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { useContractAgreements } from '@/components/agreements/useContractAgreements';
import {
    AgreementSealingProvider,
    useAgreementSealing,
} from '@/components/agreements/useAgreementSealing';
import { resolveAdvanceDeepLink } from '@/components/contracts/setup/advance/advanceDeepLink';
import { advanceAreaPath } from '@/components/contracts/setup/advance/advanceSteps';
import { resolveInsuranceDeepLink } from '@/components/contracts/setup/insurance/insuranceDeepLink';
import {
    insuranceAreaPath,
    type InsuranceArea,
} from '@/components/contracts/setup/insurance/insuranceSteps';
import { captureError } from '@/lib/logger';
import { usePermissions } from '@/hooks/usePermissions';
import { contractInspectionPath } from '@/lib/inspections';
import { coverExpiryStatus } from '@/lib/coverExpiry';

/**
 * One row of `overdue.weeks[].sales` — a repayment as the overdue engine priced it.
 *
 * `id` is `repayments.id`, except for the SYNTHETIC MONDAY ROW the engine mints to accrue a week
 * that was never paid: that one carries `id: -1` and is rendered as "(Monday accrual)".
 */
export interface OverdueSale {
    id: number;
    amount: number;
    /** 'yyyy-MM-dd' — the Ghana calendar day, not an instant. Parse with `parseISO`. */
    paid_at: string;
    description: string | null;
    remarks: string | null;
    overdue_interest: number | null;
    apply_overdue_interest: boolean;
    surplus_overdue_interest: number | null;
}

export interface OverdueWeek {
    /** 'yyyy-MM-dd' Monday of the week. */
    monday: string;
    /** Contract instalment plus every loan instalment active that week. */
    expected: number;
    total_paid: number;
    /** Sum over ALL the week's sales, waived ones included — the render filter reads this. */
    total_interest: number;
    sales: OverdueSale[];
}

/**
 * The overdue engine's payload, computed by `calculate_overdue_interest` inside
 * `get_contract_insights`. This app no longer computes overdue interest: the calculator that used
 * to live in `src/lib/overdue-interest` moved into the database so one implementation serves every
 * client (admin, driver, the parity chain).
 */
export interface ContractOverdue {
    /** Tripwire only: `contracts.overdue_interest_rate` is NOT NULL DEFAULT 365. */
    rate_missing: boolean;
    /** SIGNED — negative when interest is owed. `overdue_total` is its absolute value. */
    total_overdue_interest: number;
    /** 'yyyy-MM-dd' Monday of the first fully-paid week; interest starts the Monday after. */
    first_full_payment_week: string | null;
    weeks: OverdueWeek[];
}

// Type for the RPC response
export interface ContractInsightsData {
    contract: {
        id: number;
        signed_at: string;
        sold_at: number;
        deposit_id: number | null;
        driver_id: number;
        vehicle_id: number;
        created_at: string;
        overdue_interest_rate: number | null;
        management_rate: number | null;
    } | null;
    current_status: string | null;
    driver: {
        id: string;
        driver_id: number;
        first_name: string;
        middle_name: string | null;
        last_name: string;
        phone: string | null;
        photo: string | null;
        address: string | null;
    } | null;
    vehicle: {
        id: number;
        make: string;
        model: string;
        vin: string | null;
        color: string | null;
        manufactured_at: number | null;
        registration_number: string | null;
        tracker?: {
            id: number;
            serial_number: string | null;
            sim_number: string | null;
        } | null;
    } | null;
    repayments: Array<{
        id: number;
        description: string | null;
        transaction_id: number;
        amount: number;
        paid_at: string;
    }>;
    repayments_total: number;
    payments_count: number;
    /*
     * ── The five protected numbers, all server-computed ──
     * `weekly_rate` always was. The other four arrived with the overdue port: the app used to
     * derive them client-side from this payload's parts, which is how two apps could disagree
     * with each other and with the parity chain. Read them, never recompute them.
     */
    weekly_rate: number;
    overdue: ContractOverdue;
    /** |overdue.total_overdue_interest| — the figure that enters `total_value`. */
    overdue_total: number;
    total_value: number;
    total_paid: number;
    /** Clamped at 0: a contract cannot owe a negative balance. */
    outstanding: number;
    allocation_summary: {
        contract: number;
        loans: number;
        insurance_loans: number;
    } | null;
    loans: Array<{
        id: number;
        interest_rate: number | null;
        description: string | null;
        amount: number;
        lent_at: string;
        agreement: { signed_at: string | null; agreement_url: string | null } | null;
        instalments: Array<{ amount: number; starts_at: string }> | null;
        repayments_total: number;
        allocated_total: number;
        recent_allocations: Array<{
            repayment_id: number;
            amount: number;
            payment_amount: number;
            payment_date: string;
        }>;
        allocation_count: number;
        repayments: Array<{ id: number; amount: number; paid_at: string }>;
    }>;
    insurances: Array<{
        id: number;
        interest_rate: number | null;
        valued_at: number | null;
        starts_at: string | null;
        expires_at: string | null;
        certificate_url: string | null;
        loan: {
            id: number;
            amount: number;
            interest_rate: number | null;
            repayments_total: number;
            deposit_total: number;
            allocated_total: number;
            recent_allocations: Array<{
                repayment_id: number;
                amount: number;
                payment_amount: number;
                payment_date: string;
            }>;
            allocation_count: number;
            agreement_url: string | null;
            instalments: Array<{ amount: number; starts_at: string }> | null;
            repayments: Array<{ id: number; transaction_id: number; amount: number; paid_at: string; description: string | null; is_deposit?: boolean }>;
        } | null;
        premium: number | null;
    }>;
    maintenances: Array<{
        id: number;
        type: string | null;
        description: string | null;
        amount: number;
        started_at: string;
    }>;
    // `inspections` is still in the payload, but nothing reads it: inspections come from
    // get_contract_inspections (the monthly-inspection migration leaves this function untouched).
    deposit: {
        /** NET: credits minus refunds. Null when the contract has no deposit at all. */
        amount: number | null;
        count: number;
        /** Gross credits only — refunds live in `refunds`, never as negative payments. */
        payments: Array<{ id: number; amount: number; paid_at: string }> | null;
        /** Gross paid before refunds. */
        paid_total: number | null;
        refunded_total: number | null;
        refund_count: number;
        status: 'PAID' | 'PARTIALLY_REFUNDED' | 'REFUNDED' | null;
        refunds: Array<{
            id: number;
            deposit_id: number;
            transaction_id: number;
            amount: number;
            refunded_at: string;
            description: string | null;
            remarks: string | null;
        }>;
    } | null;
    repayment_notes: Array<{
        id: number;
        period_start: string;
        period_end: string;
        reason: string | null;
        created_at: string;
    }>;
    status_history: Array<{
        status: string;
        created_at: string;
        occurred_at: string | null;
        reason: string | null;
    }>;
    witnesses: Array<{
        id: number;
        name: string;
        address: string | null;
        phone: string | null;
    }>;
    agreements: Array<{
        type: string;
        signed_at: string | null;
        agreement_url: string | null;
    }>;
    instalments: Array<{
        id: number;
        amount: number;
        starts_at: string;
    }>;
    driver_identification: Array<{
        id: number;
        type: 'DRIVING_LICENCE' | 'NATIONAL_ID';
        document_url: string | null;
        expires_at: string | null;
    }>;
    vehicle_roadworthiness: Array<{
        id: number;
        expires_at: string;
        certificate_url: string | null;
    }>;
    loan_agreements: Array<{
        id: number;
        loan_id: number;
        signed_at: string | null;
        agreement_url: string | null;
        loan_description: string | null;
        loan_amount: number;
    }>;
    insurance_loan_agreements: Array<{
        id: number;
        loan_id: number;
        signed_at: string | null;
        agreement_url: string | null;
        insurance_id: number;
    }>;
    vehicle_trackers: Array<{
        id: number;
        type: 'primary' | 'secondary';
        serial_number: string | null;
        sim_number: string | null;
        created_at: string;
    }>;
    vehicle_registrations: Array<{
        id: number;
        number: string;
        type: string;
        certificate_url: string | null;
        transaction_id: number | null;
    }>;
    deposit_agreements: Array<{
        id: number;
        contract_deposit_id: number;
        signed_at: string | null;
        agreement_url: string | null;
        deposit_amount: number;
        deposit_date: string;
    }>;
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

/**
 * The eleven tabs, as the triggers below spell them.
 *
 * THIS LIST IS WHAT MAKES `?tab=` SAFE TO PUT IN A LINK. Radix `Tabs` is controlled here, and a
 * `value` no `TabsContent` matches draws the strip with nothing underneath it — so a hand-edited
 * address, a stale bookmark or a renamed tab lands on Summary rather than on an empty page. Keep it
 * in step with the triggers: a tab added below and forgotten here is a tab no link can reach.
 */
const TAB_VALUES = [
    'summary',
    'payments',
    'interest',
    'vehicle',
    'insurance',
    'loans',
    'maintenance',
    'roadworthiness',
    'inspections',
    'trackers',
    'documents',
];

const getStatusBadge = (status: string | null) => {
    switch (status) {
        case 'IN_PROGRESS':
            return { label: 'Active', className: 'border-green-200 bg-green-50 text-green-700' };
        case 'COMPLETED':
            return { label: 'Completed', className: 'border-blue-200 bg-blue-50 text-blue-700' };
        case 'TERMINATED':
            return { label: 'Terminated', className: 'border-red-200 bg-red-50 text-red-700' };
        default:
            return { label: 'Draft', className: 'border-gray-200 bg-gray-50 text-gray-600' };
    }
};

export default function ContractInsights() {
    const { contractId } = useParams<{ contractId: string }>();
    const navigate = useNavigate();

    /**
     * THE TAB IS IN THE URL [D-B48], AND TWO WORKFLOWS ARE THE REASON FOR IT.
     *
     * The Agreements tab on a partner was the first: its whole workflow leaves the page — every row
     * opens a contract's agreements area — and with the tab in component state alone, Back landed
     * on Summary every time. `PartnerDetail.tsx` carries the same arrangement and the same reason.
     *
     * THE ADVANCE DOSSIER IS THE SECOND, and it is the reason this page needed it too. Adding or
     * editing an advance used to be a side sheet over this tab; it is now a full-screen dossier at
     * `/contracts/:id/advances/:loanId`, and its "Back to Advances" link — and the browser's own
     * Back — have to return to the Advances tab rather than to the Summary. A tab that lives only
     * in component state cannot be returned to.
     *
     * THE PARAM IS THE STATE RATHER THAN A COPY OF IT, so there is nothing to keep in step. An
     * unknown or absent `?tab=` reads as `summary`, which is what a bare link should open — and an
     * unknown one is not silently kept in the address bar as a tab nobody is on: it is VALIDATED
     * against the triggers below, because `Tabs` with a `value` no `TabsContent` matches renders a
     * page with a tab strip and nothing under it.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');
    const activeTab = requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'summary';
    const setActiveTab = (tab: string) => {
        const next = new URLSearchParams(searchParams);
        if (tab === 'summary') next.delete('tab');
        else next.set('tab', tab);
        // REPLACE, NOT PUSH: a tab is where you are on this page, not a page of its own, and
        // pushing would make Back walk every tab an admin glanced at.
        setSearchParams(next, { replace: true });
    };

    // Sheet states
    const [manageOpen, setManageOpen] = useState(false);

    // Maintenance sheet state
    const [maintenanceSheetOpen, setMaintenanceSheetOpen] = useState(false);
    const [editMaintenanceId, setEditMaintenanceId] = useState<number | null>(null);

    // The inspection pages sit under the `inspections` key; Manage offers them only to an admin who holds it.
    const { canAccessRoute } = usePermissions();
    const canOpenInspections = canAccessRoute('/dashboard/inspections');

    // Roadworthiness sheet state
    const [roadworthinessSheetOpen, setRoadworthinessSheetOpen] = useState(false);
    const [editRoadworthinessId, setEditRoadworthinessId] = useState<number | null>(null);

    // Tracker sheet state
    const [trackerSheetOpen, setTrackerSheetOpen] = useState(false);

    // Terminate dialog state
    const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['contract-insights', contractId],
        queryFn: async () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase as any).rpc('get_contract_insights', {
                contract_id_param: parseInt(contractId || '0', 10),
            });
            if (error) throw error;
            return data as ContractInsightsData;
        },
        enabled: !!contractId,
    });

    const driverPhotoUrl = useSignedUrl(data?.driver?.photo);

    const handleRefresh = () => {
        refetch();
    };

    /**
     * THE SIGNING MODEL FOR EVERY POLICY YEAR ON THIS CONTRACT [D-B45, R8].
     *
     * `junctionScope: 'all'` IS THE WHOLE DIFFERENCE FROM THE SETUP CANVAS, and it is why this door
     * exists. The setup board mints insurance slots for the ID-EARLIEST junction alone [D-B17] —
     * widening it there is what produced contract 137's "✓ and Not on file" contradiction, where
     * one manifest row graded a sealed renewal loan while its detail described the unpapered
     * original — so a renewal year has no door at all on that canvas. It has one here, on the tab
     * that is already about every year of cover, under a cache key of its own.
     *
     * DECLARED ONCE, AT PAGE LEVEL, AND HANDED DOWN. This hook owns seven query keys; a second
     * declaration inside the tab would register a second query function against keys this one
     * already owns, which is the failure that made the insurance read return two different shapes
     * for one entry.
     *
     * `onSuccess` IS THE INSIGHTS PAYLOAD'S OWN REFETCH. Generating, sealing or voiding a document
     * moves `insurance_loan_agreements.signed_at`/`agreement_url`, which is what this tab's "Needs
     * Agreement" badge and its paperclip read — so the board's refresh has to reach the RPC the
     * page is drawn from, not only the board's own caches.
     *
     * READINESS IS UNMOVED. Nothing on this page is a setup row: [D-B17] keeps renewals invisible
     * to `get_contract_setup_status`, and this is a signing door, not a gate.
     */
    const agreements = useContractAgreements({
        contractId: parseInt(contractId || '0', 10),
        junctionScope: 'all',
        onSuccess: handleRefresh,
    });

    /**
     * ONE SEALING LATCH FOR THE WHOLE PAGE — `ContractSetup.tsx:302`'s arrangement, for the same
     * reason and against a sharper version of the same failure.
     *
     * WITHOUT IT EVERY `AgreementSlotList` TAKES A PRIVATE ONE, and on this page that list is
     * mounted INSIDE the per-card `AnimatePresence` — so the latch is not merely duplicated, it is
     * REMOUNTED on every expansion. `finalizeAttempted` is a `useRef` Set, so each expand re-fires
     * `agreement_finalize` for every COMPLETED-unsealed document on the contract and re-toasts a
     * seal that is failing for a reason nobody has fixed. And the exit animation keeps the outgoing
     * card's list mounted for 200ms while the next card's mounts, so two private latches genuinely
     * overlap: the second call lands on `AGREEMENT_FINALIZE_IN_PROGRESS`, which its row then holds
     * as a failed seal needing a human.
     *
     * KEYED OFF THE DOCUMENTS, NOT OFF ANY CARD'S SLOTS, exactly as the setup page keys it: a
     * COMPLETED document no expanded card claims is one nobody would seal at all.
     */
    const sealing = useAgreementSealing({
        documents: agreements.documents,
        onRefresh: agreements.refreshBoard,
    });

    /**
     * The advance dossier, on the area that owns whatever was asked for.
     *
     * ONE BUILDER FOR EVERY DOOR ONTO IT — Add, Edit, the Manage sheet's own entry and the card's
     * agreement CTA — so a rename of the route cannot half-land across four call sites.
     */
    const openAdvance = (loan: number | 'new', area: 'disbursement' | 'agreement') =>
        navigate(advanceAreaPath(parseInt(contractId || '0', 10), loan, area));

    /**
     * THE ADVANCES TAB GETS ITS OWN DEEP LINK, NEVER THE BARE SETUP ROUTE.
     *
     * A LOAN slot's pre-flight names `loan.` merge fields, and those are edited on the advance
     * dossier rather than anywhere in setup — the setup vocabulary holds no key for any of them, so
     * the bare setup route is the best it could answer, and the admin would be sent to the top of
     * the contract's setup page to fix a field that is not there.
     * `resolveAdvanceDeepLink` asks the advance vocabulary first and falls through to setup for the
     * twelve keys that really do belong to the contract, so every field the Schedule quotes has
     * somewhere to go. It is TOTAL: an unknown key lands on the bare setup route rather than
     * nowhere.
     *
     * IT TAKES THE LOAN AS WELL AS THE AREA, because on this page an advance area is not a place on
     * its own — it is a place on ONE advance's dossier, and the card knows which.
     */
    const openAdvanceDeepLink = (loanId: number, area: string) => {
        const id = parseInt(contractId || '0', 10);
        const target = resolveAdvanceDeepLink(id, area);
        if (target.kind === 'advance') {
            navigate(advanceAreaPath(id, loanId, target.area));
            return;
        }
        navigate(target.path);
    };

    /**
     * The policy dossier, on the area that owns whatever was asked for.
     *
     * ONE BUILDER FOR EVERY DOOR ONTO IT — Add, the card's pencil and the Manage sheet's own entry
     * — so a rename of the route cannot half-land across three call sites. The side sheet it
     * replaces was a component this page mounted with an id in state; a year of cover is a page
     * now, so the id is in the address and Back comes back to this tab.
     */
    const openPolicy = (policy: number | 'new', area: InsuranceArea) =>
        navigate(insuranceAreaPath(parseInt(contractId || '0', 10), policy, area));

    /**
     * THE INSURANCE TAB GETS ITS OWN DEEP LINK, AND IT CARRIES THE FIELD AS WELL AS THE STEP.
     *
     * `MERGE_FIELD_STEPS` routes the whole `insurance.` and `insurance_loan.` namespaces to the
     * single step `insurance` — right while insurance was one area of contract setup, and three
     * canvases now. `resolveInsuranceDeepLink` splits that step by field and falls through to the
     * contract's own setup route for everything else, so every key an instrument quotes has
     * somewhere to go; it is TOTAL, so an unknown one lands on the bare setup route rather than
     * nowhere.
     *
     * IT TAKES THE POLICY AS WELL, because on this page an insurance area is not a place on its own
     * — it is a place on ONE year of cover's dossier, and the card knows which.
     */
    const openInsuranceDeepLink = (policyId: number, step: string, field?: string) => {
        const id = parseInt(contractId || '0', 10);
        const target = resolveInsuranceDeepLink(id, step, field);
        if (target.kind === 'insurance') {
            navigate(insuranceAreaPath(id, policyId, target.area));
            return;
        }
        navigate(target.path);
    };

    // Scroll to top when page loads to prevent inheriting scroll position from previous page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (isLoading) {
        return (
            <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (error || !data?.contract) {
        return (
            <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
                <nav className="flex items-center gap-2 text-sm">
                    <button
                        onClick={() => navigate('/dashboard/contracts')}
                        className="text-primary hover:underline"
                    >
                        Contracts
                    </button>
                    <span className="text-muted-foreground">{'>'}</span>
                    <span className="text-muted-foreground">Contract</span>
                </nav>
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <FileText className="h-12 w-12 mb-4" />
                    <p className="text-lg">Contract not found</p>
                    <p className="text-sm">The contract you're looking for doesn't exist or has been removed.</p>
                </div>
            </div>
        );
    }


    const statusBadge = getStatusBadge(data.current_status);
    const driverName = data.driver
        ? [data.driver.first_name, data.driver.middle_name, data.driver.last_name].filter(Boolean).join(' ') || 'Unknown Driver'
        : 'Unknown Driver';
    const vehicleDisplay = data.vehicle?.registration_number || `${data.vehicle?.make || ''} ${data.vehicle?.model || ''}`.trim() || 'No Vehicle';

    // Total contract value — vehicle, loan principal and interest, insurance loan principal and
    // interest, and overdue interest — summed by `get_contract_insights`. The header used to
    // reassemble it from the payload's parts, and the Overview tab used to reassemble it again.
    const totalContractValue = data.total_value;

    const insuranceTabState: 'ok' | 'warning' | 'critical' = (() => {
        const latest = (data.insurances || [])[0];
        if (!latest) return 'ok';
        // Check for expired first (critical)
        // Good through its expiry date, on the Ghana day (G6): the one rule, in `coverExpiryStatus`.
        const expiry = coverExpiryStatus(latest.expires_at);
        if (expiry === 'expired') return 'critical';
        if (expiry === 'expiring') return 'warning';
        // Missing docs (warning)
        if (!latest.certificate_url) return 'warning';
        if (latest.loan && !latest.loan.agreement_url) return 'warning';
        return 'ok';
    })();

    return (
        // THE PROVIDER WRAPS THE WHOLE PAGE, so every `AgreementSlotList` a policy card mounts —
        // and every one a future tab mounts — joins the latch above instead of taking its own.
        <AgreementSealingProvider value={sealing}>
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Breadcrumb */}
            <div className="flex items-center justify-between">
                <nav className="flex items-center gap-2 text-sm">
                    <button
                        onClick={() => navigate('/dashboard/contracts')}
                        className="text-primary hover:underline"
                    >
                        Contracts
                    </button>
                    <span className="text-muted-foreground">{'>'}</span>
                    <span className="text-muted-foreground">Contract #{data.contract.id}</span>
                </nav>
                <Button onClick={() => setManageOpen(true)} variant="outline" size="sm">
                    <Settings className="h-4 w-4 mr-1" />
                    Manage
                </Button>
            </div>

            {/* Premium Header */}
            <div className="border rounded-xl p-6 bg-gradient-to-br from-background via-background to-muted/30 backdrop-blur-sm">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    {/* Left: Driver Info */}
                    <div className="flex items-start gap-4">
                        {/* Driver Photo - sized to align with name row at top and badges at bottom */}
                        {driverPhotoUrl ? (
                            <img
                                src={driverPhotoUrl}
                                alt={driverName}
                                className="h-[88px] w-[88px] rounded-full object-cover border-4 border-background shadow-lg ring-2 ring-primary/20"
                            />
                        ) : (
                            <div className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5 shadow-lg ring-2 ring-primary/20">
                                <User className="h-11 w-11 text-primary" />
                            </div>
                        )}
                        <div className="space-y-2">
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl font-bold tracking-tight">{driverName}</h1>
                                <Badge
                                    variant="outline"
                                    className={`${statusBadge.className} ${data.current_status === 'IN_PROGRESS' ? 'animate-pulse' : ''}`}
                                >
                                    {statusBadge.label}
                                </Badge>
                            </div>
                            {data.driver?.phone && (
                                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                                    <Phone className="h-3.5 w-3.5" />
                                    {data.driver.phone}
                                </p>
                            )}
                            <div className="flex flex-wrap gap-3 text-xs">
                                <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50">
                                    <FileText className="h-3 w-3" />
                                    Contract #{data.contract.id}
                                </span>
                                <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50">
                                    <Car className="h-3 w-3" />
                                    {vehicleDisplay}
                                </span>
                                {data.contract.signed_at && new Date(data.contract.signed_at).getFullYear() > 1970 && (
                                    <span className="px-2 py-1 rounded-full bg-muted/50">
                                        Signed {format(new Date(data.contract.signed_at), 'MMM d, yyyy')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: Total Value */}
                    <div className="text-right flex flex-col justify-between self-stretch">
                        <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Value</p>
                            {/* No second loading state: the total arrives with the insights payload
                                this page already waited for, so it is never briefly wrong. */}
                            <p className="text-3xl font-bold mt-1"><CurrencyDisplay amount={totalContractValue} /></p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {data.weekly_rate > 0 && `GHS ${data.weekly_rate.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 20 })}/week`}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="flex-wrap">
                    <TabsTrigger value="summary">Summary</TabsTrigger>
                    <TabsTrigger value="payments">Payments</TabsTrigger>
                    <TabsTrigger value="interest">Interest</TabsTrigger>
                    <TabsTrigger value="vehicle">Vehicle</TabsTrigger>
                    <TabsTrigger value="insurance" className="relative">
                        Insurance
                        {insuranceTabState === 'critical' && (
                            <AlertOctagon className="h-3.5 w-3.5 text-red-500 ml-1" />
                        )}
                        {insuranceTabState === 'warning' && (
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 ml-1" />
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="loans">Advances</TabsTrigger>
                    <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
                    <TabsTrigger value="roadworthiness">Roadworthiness</TabsTrigger>
                    <TabsTrigger value="inspections">Inspections</TabsTrigger>
                    <TabsTrigger value="trackers">Trackers</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                </TabsList>

                <TabsContent value="summary">
                    <ContractInsightOverview data={data} />
                </TabsContent>

                <TabsContent value="payments">
                    <ContractInsightPayments data={data} />
                </TabsContent>

                <TabsContent value="interest">
                    <ContractInsightInterest data={data} />
                </TabsContent>

                <TabsContent value="vehicle">
                    <ContractInsightVehicle data={data} />
                </TabsContent>

                <TabsContent value="loans">
                    <ContractInsightLoans
                        data={data}
                        contractId={data.contract.id}
                        agreements={agreements}
                        /* THE SIDE SHEET IS GONE. Both doors open the advance dossier on its
                           disbursement area — the create flow under the literal `new`, an edit
                           under the advance's own id. */
                        onAddLoan={() => openAdvance('new', 'disbursement')}
                        onEditLoan={(id) => openAdvance(id, 'disbursement')}
                        onDeepLink={openAdvanceDeepLink}
                        onRefresh={handleRefresh}
                    />
                </TabsContent>

                <TabsContent value="insurance">
                    <ContractInsightInsurance
                        data={data}
                        contractId={data.contract.id}
                        vehicleId={data.contract.vehicle_id}
                        agreements={agreements}
                        onDeepLink={openInsuranceDeepLink}
                        onRefresh={agreements.refreshBoard}
                        /* THE SIDE SHEET IS GONE. Both doors open the policy dossier on its Policy
                           area — the create flow under the literal `new`, an edit under the
                           policy's own id. */
                        onAddInsurance={() => openPolicy('new', 'policy')}
                        onEditInsurance={(id) => openPolicy(id, 'policy')}
                    />
                </TabsContent>

                <TabsContent value="maintenance">
                    <ContractInsightMaintenance
                        data={data}
                        onAddMaintenance={() => {
                            setEditMaintenanceId(null);
                            setMaintenanceSheetOpen(true);
                        }}
                        onEditMaintenance={(id) => {
                            setEditMaintenanceId(id);
                            setMaintenanceSheetOpen(true);
                        }}
                    />
                </TabsContent>

                <TabsContent value="roadworthiness">
                    <ContractInsightRoadworthiness
                        data={data}
                        onAddRoadworthiness={() => {
                            setEditRoadworthinessId(null);
                            setRoadworthinessSheetOpen(true);
                        }}
                        onEditRoadworthiness={(id) => {
                            setEditRoadworthinessId(id);
                            setRoadworthinessSheetOpen(true);
                        }}
                    />
                </TabsContent>

                <TabsContent value="inspections">
                    <ContractInsightInspections contractId={parseInt(contractId || '0', 10)} />
                </TabsContent>

                <TabsContent value="trackers">
                    <ContractInsightTrackers
                        data={data}
                        onManageTrackers={() => setTrackerSheetOpen(true)}
                    />
                </TabsContent>

                <TabsContent value="documents">
                    <ContractInsightDocuments
                        data={data}
                        vehicleId={data.contract?.vehicle_id ?? 0}
                        onRefresh={() => refetch()}
                    />
                </TabsContent>
            </Tabs>

            <ManageContractSheet
                open={manageOpen}
                onOpenChange={setManageOpen}
                onOpenSetup={() => navigate(`/dashboard/contracts/${contractId}/setup`)}
                onOpenInsurance={() => openPolicy('new', 'policy')}
                onOpenLoan={() => openAdvance('new', 'disbursement')}
                onOpenMaintenance={() => {
                    setEditMaintenanceId(null);
                    setMaintenanceSheetOpen(true);
                }}
                onOpenInspection={
                    canOpenInspections ? () => navigate(contractInspectionPath(parseInt(contractId || '0', 10))) : undefined
                }
                onOpenRoadworthiness={() => {
                    setEditRoadworthinessId(null);
                    setRoadworthinessSheetOpen(true);
                }}
                onManageTrackers={() => {
                    setTrackerSheetOpen(true);
                }}
                currentStatus={data.current_status}
                onMarkComplete={() => setCompleteDialogOpen(true)}
                onUndoComplete={async () => {
                    const { data: result, error } = await supabase.rpc('undo_complete_contract', {
                        contract_id_param: parseInt(contractId || '0', 10),
                    });
                    if (error) {
                        captureError(error);
                        toast.error(error.message || 'Failed to undo completion');
                        return;
                    }
                    if (!(result as any)?.success) {
                        toast.error((result as any)?.error || 'Failed to undo completion');
                        return;
                    }
                    toast.success('Contract reactivated successfully');
                    refetch();
                }}
                onTerminate={() => setTerminateDialogOpen(true)}
                onUndoTerminate={async () => {
                    const { data: result, error } = await supabase.rpc('undo_terminate_contract', {
                        contract_id_param: parseInt(contractId || '0', 10),
                    });
                    if (error) {
                        captureError(error);
                        toast.error(error.message || 'Failed to undo termination');
                        return;
                    }
                    if (!(result as any)?.success) {
                        toast.error((result as any)?.error || 'Failed to undo termination');
                        return;
                    }
                    toast.success('Contract reactivated successfully');
                    refetch();
                }}
            />

            <TerminateContractDialog
                open={terminateDialogOpen}
                onOpenChange={setTerminateDialogOpen}
                contractId={parseInt(contractId || '0', 10)}
                onSuccess={() => refetch()}
            />

            <CompleteContractDialog
                open={completeDialogOpen}
                onOpenChange={setCompleteDialogOpen}
                contractId={parseInt(contractId || '0', 10)}
                onSuccess={() => refetch()}
            />

            <AddMaintenanceSheet
                open={maintenanceSheetOpen}
                onOpenChange={(open) => {
                    setMaintenanceSheetOpen(open);
                    if (!open) setEditMaintenanceId(null);
                }}
                vehicleId={data.contract.vehicle_id}
                contractId={parseInt(contractId || '0', 10)}
                maintenanceId={editMaintenanceId}
                onSuccess={handleRefresh}
            />

            <AddRoadworthinessWizard
                open={roadworthinessSheetOpen}
                onOpenChange={(open) => {
                    setRoadworthinessSheetOpen(open);
                    if (!open) setEditRoadworthinessId(null);
                }}
                vehicleId={data.contract.vehicle_id}
                contractId={parseInt(contractId || '0', 10)}
                roadworthinessId={editRoadworthinessId}
                onSuccess={handleRefresh}
            />

            <TrackerForm
                open={trackerSheetOpen}
                onOpenChange={setTrackerSheetOpen}
                vehicleId={data.contract.vehicle_id}
                onSuccess={handleRefresh}
            />
        </div >
        </AgreementSealingProvider>
    );
}
